import React, { useState } from "react"
import { Button, Form, Modal } from "react-bootstrap"
import { injectIntl } from "react-intl"
import LoadingSpinner from "../../common/spinner/loading.component"
import { GlobalHelper } from "../../indexImports"
import PaymentAccount from "../../payment-account/model/paymentaccount"
import { Intl } from "../../state/helpers/IntlProps"
import TerminalService from "../../state/terminal/TerminalService"
import TestModeAlert from "../components/TestModeAlert"
import { messages } from "./TerminalMessages"
import "./modals.scss"

type Props = {
    intl: Intl,
    show: boolean,
    paymentAccount: PaymentAccount,
    onClose: (success: boolean) => void,
}

const TERMINAL_REGISTRATION_CODE_TEST_MODE = "VIRTUAL_DEVICE"

const RegisterTerminalModal = (props: Props) => {
    const [registrationCodeError, setRegistrationCodeError] = useState<boolean>(false)
    const [nameError, setNameError] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const formRef = React.createRef<HTMLFormElement>()
    const registrationCodeInputRef = React.createRef<HTMLInputElement>()
    const nameInputRef = React.createRef<HTMLInputElement>()

    const handleClose = (success = false) => {
        setRegistrationCodeError(false)
        setNameError(false)
        props.onClose(success)
    }

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault()
        validateFields()

        if (isFormValid()) {
            registerDevice()
        }
    }

    const registerDevice = async () => {
        setIsLoading(true)
        const res = await TerminalService.post(
            props.paymentAccount.getId(),
            getRegistrationCode(),
            getName()
        )
        setIsLoading(false)

        if (res.isLoaded()) {
            handleClose(true)
        }
    }

    const validateFields = () => {
        validateRegistrationCode()
        validateName()
    }

    const getRegistrationCode = (): string => {
        
        if(GlobalHelper.isPaymentGatewayTestMode()){
            return TERMINAL_REGISTRATION_CODE_TEST_MODE
        }

        return registrationCodeInputRef.current?.value.trim() ?? ""
    }

    const validateRegistrationCode = () => {
        const value = getRegistrationCode()
        setRegistrationCodeError(!value.length)
    }

    const getName = (): string => {
        return nameInputRef.current?.value.trim() ?? ""
    }

    const validateName = () => {
        const value = getName()
        setNameError(!value.length || value.length > 20)
    }

    const isFormValid = (): boolean => {
        return (formRef.current?.checkValidity() ?? false) && !registrationCodeError && !nameError
    }

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Form ref={formRef} noValidate onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {props.intl.formatMessage(messages.registerModalHeader)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <TestModeAlert />
                    <Form.Group>
                        <Form.Label>
                            {props.intl.formatMessage(messages.deviceRegistrationCodeLabel)}
                        </Form.Label>
                        <Form.Control
                            ref={registrationCodeInputRef}
                            required
                            type="text"
                            placeholder={GlobalHelper.isPaymentGatewayTestMode() ? 
                                props.intl.formatMessage(messages.deviceRegistrationCodeTestModePlaceholder) :
                                props.intl.formatMessage(messages.deviceRegistrationCodePlaceholder)
                            }
                            onChange={validateRegistrationCode}
                            isValid={!registrationCodeError && !!getRegistrationCode()}
                            isInvalid={registrationCodeError}
                            disabled={isLoading || GlobalHelper.isPaymentGatewayTestMode()}
                        />
                        <Form.Control.Feedback type="invalid">
                            {props.intl.formatMessage(messages.deviceRegistrationCodeMissing)}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            {props.intl.formatMessage(messages.deviceRegistrationCodeHint)}
                        </Form.Text>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>
                            {props.intl.formatMessage(messages.deviceNameLabel)}
                        </Form.Label>
                        <Form.Control
                            ref={nameInputRef}
                            required
                            type="text"
                            placeholder={
                                props.intl.formatMessage(messages.deviceNamePlaceholder)
                            }
                            maxLength={20}
                            onChange={validateName}
                            isValid={!nameError && !!getName()}
                            isInvalid={nameError}
                            disabled={isLoading}
                        />
                        <Form.Control.Feedback type="invalid">
                            {props.intl.formatMessage(messages.deviceNameMissing)}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                            {props.intl.formatMessage(messages.deviceNameHint)}
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => handleClose()} disabled={isLoading}>
                        {props.intl.formatMessage(messages.registerModalClose)}
                    </Button>
                    <Button variant="primary" type="submit" disabled={isLoading}>
                        {isLoading && <LoadingSpinner height={18} />}
                        {!isLoading && <span>{props.intl.formatMessage(messages.registerModalSubmit)}</span>}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    )
}

export default injectIntl(RegisterTerminalModal)


// WEBPACK FOOTER //
// ./src/payment-processing/modals/RegisterTerminalModal.tsx